import { getBufferReader } from '../read-json-stream/utils';

export type ParsedSSEMessage = {
	eventType?: string;
	data?: string;
	id?: string;
};

const parseSSEMessage = (message: string): ParsedSSEMessage => {
	const parsed: ParsedSSEMessage = {};
	const dataLines: string[] = [];

	for (const line of message.split('\n')) {
		const trimmedLine = line.trim();
		const colonIndex = trimmedLine.indexOf(':');

		if (trimmedLine && colonIndex !== -1) {
			const field = trimmedLine.substring(0, colonIndex);
			const value = trimmedLine.substring(colonIndex + 1).trimStart();

			switch (field) {
				case 'event':
					parsed.eventType = value;
					break;
				case 'data':
					dataLines.push(value);
					break;
				case 'id':
					parsed.id = value;
					break;
				default:
					break;
			}
		}
	}

	if (dataLines.length) {
		parsed.data = dataLines.join('\n');
	}

	return parsed;
};

export async function* readSSEStream<T>(response: Response): AsyncGenerator<T> {
	const reader = getBufferReader(response);
	let doneStreaming = false;
	let remainder = '';

	do {
		try {
			const { value, done } = await reader.read();

			doneStreaming = done;
			if (value) {
				const combined = `${remainder}${value}`;
				const parts = combined.split('\n\n'); // SSE messages are separated by double newlines
				// the last part may be incomplete — keep it for the next read
				remainder = parts.pop() ?? '';

				for (const chunk of parts) {
					try {
						const parsed = parseSSEMessage(chunk);
						if (parsed.data) {
							yield JSON.parse(parsed.data) as T;
						}
					} catch {
						// ignore malformed JSON; keep streaming future valid messages
					}
				}
			}

			// If the stream just ended and we still have a remainder, try to parse it once.
			if (doneStreaming && remainder) {
				try {
					const parsed = parseSSEMessage(remainder);
					if (parsed.data) {
						yield JSON.parse(parsed.data) as T;
					}
				} catch {
					// ignore malformed tail
				} finally {
					remainder = '';
				}
			}
		} catch (e) {
			// If we abort the call, this exception bubbles up, so we want to catch it and exit gracefully
			if (e instanceof DOMException && e.name === 'AbortError') {
				doneStreaming = true;
			} else {
				throw e;
			}
		}
	} while (!doneStreaming);
}
